import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UnifiedIcon } from '@/components/UnifiedIcon';

export type SelfOnboardingSignupStepId = 'company' | 'you';

export type SelfOnboardingSignupStepperProps = {
  activeStep: SelfOnboardingSignupStepId;
};

const SIGNUP_STEPS: readonly SelfOnboardingSignupStepId[] = ['company', 'you'];

/** Rough founder effort per step (minutes). Used for remaining-time copy. */
export const SELF_ONBOARDING_SIGNUP_STEP_ETA_MINUTES: Record<
  SelfOnboardingSignupStepId,
  number
> = {
  company: 1,
  you: 2,
};

/**
 * Sum of ETA minutes for the active step and any not-yet-completed later steps.
 */
export function getSelfOnboardingSignupRemainingMinutes(
  activeStep: SelfOnboardingSignupStepId
): number {
  const currentIndex = SIGNUP_STEPS.indexOf(activeStep);
  if (currentIndex < 0) {
    return 0;
  }
  return SIGNUP_STEPS.slice(currentIndex).reduce(
    (sum, id) => sum + SELF_ONBOARDING_SIGNUP_STEP_ETA_MINUTES[id],
    0
  );
}

/**
 * Two-step progress for public company signup (company → you).
 * Completed steps show a checkmark; each step lists an estimated duration.
 */
export function SelfOnboardingSignupStepper({ activeStep }: SelfOnboardingSignupStepperProps) {
  const { t } = useTranslation();

  const currentStepNumber = SIGNUP_STEPS.indexOf(activeStep) + 1;

  const steps = useMemo(
    () =>
      SIGNUP_STEPS.map((id, index) => {
        const stepNumber = index + 1;
        const completed = currentStepNumber > stepNumber;
        const active = currentStepNumber === stepNumber;
        return {
          id,
          shortTitle: String(t(`plg.selfOnboarding.signup.steps.${id}`)),
          stepNumber,
          etaMinutes: SELF_ONBOARDING_SIGNUP_STEP_ETA_MINUTES[id],
          completed,
          active,
        };
      }),
    [currentStepNumber, t]
  );

  return (
    <nav
      aria-label={String(t('plg.selfOnboarding.signup.stepperAria'))}
      className="mb-2 flex w-full shrink-0 items-start"
      data-testid="plg-signup-stepper"
    >
        {steps.map((step, index) => (
          <Fragment key={step.id}>
            <div className="flex min-w-0 items-start">
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold ${
                  step.completed || step.active
                    ? 'border-indigo-600 bg-indigo-600 text-white dark:border-indigo-400 dark:bg-indigo-400 dark:text-slate-900'
                    : 'border-slate-300 bg-white text-slate-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-400'
                }`}
                data-testid={
                  step.completed
                    ? `plg-signup-step-${step.id}-complete`
                    : `plg-signup-step-${step.id}`
                }
                aria-current={step.active ? 'step' : undefined}
              >
                {step.completed ? (
                  <UnifiedIcon name="Check" library="phosphor" className="h-4 w-4" />
                ) : (
                  step.stepNumber
                )}
              </div>
              <div className="ml-2 flex min-w-0 flex-col">
                <span
                  className={`text-sm font-semibold ${
                    step.completed || step.active
                      ? 'text-slate-800 dark:text-slate-100'
                      : 'text-slate-500 dark:text-slate-400'
                  }`}
                >
                  {step.shortTitle}
                </span>
                <span
                  className={`mt-0.5 text-xs ${
                    step.completed
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-slate-500 dark:text-slate-400'
                  }`}
                  data-testid={`plg-signup-step-${step.id}-eta`}
                >
                  {step.completed
                    ? t('plg.selfOnboarding.signup.stepDone')
                    : t('plg.selfOnboarding.signup.etaMinutes', { count: step.etaMinutes })}
                </span>
              </div>
            </div>
            {index < steps.length - 1 ? (
              <div
                className={`mx-4 mt-4 h-0.5 min-w-[1.5rem] flex-1 ${
                  step.completed
                    ? 'bg-indigo-600 dark:bg-indigo-400'
                    : 'bg-slate-200 dark:bg-slate-600'
                }`}
                aria-hidden
              />
            ) : null}
          </Fragment>
        ))}
    </nav>
  );
}
